import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    forwardRef, Inject,
    Input,
    OnDestroy,
    OnInit,
    Output,
    QueryList,
    ViewChild,
    ViewChildren,
    ViewEncapsulation
} from '@angular/core';
import { NG_VALUE_ACCESSOR, NgModel } from '@angular/forms';
import {
    MODAL_EDITOR_TOKEN,
    ModalEditorBase,
} from '../../../classes/modal-editor-base';
import { EditorLocation } from 'apps/account/src/account-shared';
import { filter, map, takeUntil, tap } from 'rxjs/operators';
// @ts-ignore
import { APILoader } from '@googlemaps/extended-component-library/api_loader.js';
import { Environment, ENVIRONMENT_TOKEN } from 'account-hybrid/core/environment.service';
import { formatAddress } from '../../../utils/address.util';

@Component({
    selector: 'ft-address-editor',
    templateUrl: './address-editor.component.html',
    styleUrls: ['./address-editor.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: forwardRef(() => AddressEditorComponent),
        },
        {
            provide: MODAL_EDITOR_TOKEN,
            useExisting: AddressEditorComponent,
        },
    ],
    encapsulation: ViewEncapsulation.None,
})
export class AddressEditorComponent
    extends ModalEditorBase
    implements AfterViewInit, OnDestroy, OnInit
{
    @Input() isStructuredLocation = false;
    @Input() isVenue: boolean;
    @Input() isManualEntry = true;
    @ViewChild('lookupRef', { static: false }) lookupRef: ElementRef;
    @ViewChild('loaderRef', { static: true }) loeaderRef: ElementRef;
    @ViewChildren('inputs', { read: NgModel }) inputs: QueryList<NgModel>;
    @Output() editorVisible = new EventEmitter();

    fullAddress: string;
    overlaySelector = 'cdk-global-overlay-wrapper';
    initialValue: EditorLocation = {
        streetAddress: null,
        countryName: null,
        region: null,
        city: null,
        postalCode: null,
        latitude: null,
        longitude: null,
        area: null,
        googlePlaceId: null
    };
    baseFields: EditorLocation = {
        ...this.initialValue,
    };

    googlePlacesModule: any;

    constructor(
        private changeDetectorRef: ChangeDetectorRef,
        @Inject(ENVIRONMENT_TOKEN) private environment: Environment
    ) {
        super();
        this.value$
            .pipe(
                tap((value) => {
                    if (!value) {
                        this.initValue();
                    }
                }),
                filter((value) => !!value),
                map((value) => ({ ...this.baseFields, ...value })),
                tap((value) => {
                    this.fullAddress = this.formatAddress(value);
                    this.updateLanguageData(value);
                }),
                takeUntil(this.destroySubj)
            )
            .subscribe();

        this.onModelInit()
            .pipe(
                tap((value) => {
                    this.initValue(value);
                }),
                takeUntil(this.destroySubj)
            )
            .subscribe();
    }

    updateLanguageData(location: EditorLocation) {
        if (location.languageData?.length === 1) {
            location.languageData[0].city = location.city;
            location.languageData[0].region = location.region;
            location.languageData[0].postalCode = location.postalCode;
            location.languageData[0].area = location.area;
            location.languageData[0].name = location.name;
            location.languageData[0].streetAddress = location.streetAddress;
            location.languageData[0].googlePlaceId = location?.googlePlaceId;
        }
    }

    formatAddress = formatAddress;

    initValue(value?: any) {
        this.value = {
            ...this.baseFields,
            ...value,
            latitude: isNaN(value?.latitude) ? null : value?.latitude,
            longitude: isNaN(value?.longitude) ? null : value?.longitude,
        };

        if (this.isVenue) {
            this.value = {
                ...this.value,
                name: this.value?.name
            };
        }
    }

    onEditorVisible() {
        super.onEditorVisible();
        document
            .getElementsByClassName(this.overlaySelector)?.[0]
            .addEventListener('scroll', this.onScroll.bind(this));
        this.editorVisible.emit();
    }

    ngOnInit() {
        this.loeaderRef.nativeElement.setAttribute('key', this.environment.googlePlacesApiKey);
        this.updateInitialValue();
    }

    initAutocomplete() {
        const componentForm = {
            street_number: 'short_name',
            route: 'long_name',
            locality: 'long_name',
            administrative_area_level_1: 'short_name',
            country: 'short_name',
            postal_code: 'short_name',
        };
        // Create the autocomplete object, restricting the search to full addresses.
        const autocomplete = new this.googlePlacesModule.Autocomplete(
            this.lookupRef.nativeElement,
            { places: this.isVenue ? [] : ['address'] }
        );

        // When the user selects an address from the dropdown, populate the address fields in the form.
        autocomplete.addListener('place_changed', () => {
            // Get the place details from the autocomplete object.
            const place = autocomplete.getPlace();

            // Get each component of the address from the place details and populate an easy to read object
            const location: any = {
                lat: place.geometry.location.lat(),
                long: place.geometry.location.lng(),
            };
            for (let i = 0; i < place.address_components.length; i++) {
                const addressType = place.address_components[i].types[0];
                if (componentForm[addressType]) {
                    location[addressType] =
                        place.address_components[i][componentForm[addressType]];
                }
            }

            // Convert our object into a location object
            let cleanLocation: Partial<EditorLocation> = {
                streetAddress:
                    (location.street_number
                        ? `${location.street_number} `
                        : '') + (location.route || '') || '',
                countryName: location.country || '',
                region: location.administrative_area_level_1 || '',
                city:
                    location.locality ||
                    place.address_components.find((item) =>
                        item.types.some(
                            (type) => type === 'administrative_area_level_1'
                        )
                    )?.long_name ||
                    '',
                postalCode: location.postal_code || '',
                latitude: location.lat || '',
                longitude: location.long || '',
                googlePlaceId: place.place_id || ''
            };

            if (this.isVenue) {
                cleanLocation = { ...cleanLocation, name: place.name || '' };
            }

            this.value = { ...this.value, ...cleanLocation };
            this.changeDetectorRef.detectChanges();
        });
    }

    async ngAfterViewInit() {
        this.googlePlacesModule = await APILoader.importLibrary('places');

        this.initAutocomplete();
    }

    ngOnDestroy() {
        super.ngOnDestroy();
        document
            .getElementsByClassName(this.overlaySelector)?.[0]
            ?.removeEventListener('scroll', this.onScroll.bind(this));
    }

    onScroll() {
        if (document.activeElement === this.lookupRef.nativeElement) {
            this.lookupRef.nativeElement.blur();
        }
    }

    isInvalid(): boolean {
        return this.inputs?.some((input) => input.invalid);
    }
}
